import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Play, Pause, Volume2, VolumeX, SkipForward } from 'lucide-react';

const FALLBACK_API_URL = 'https://taraanaya-prod-production.up.railway.app';
const API_URL = process.env.REACT_APP_BACKEND_URL || FALLBACK_API_URL;

// Domain configuration
const DOMAINS = [
  { key: 'health', name: 'Health', icon: '❤️', color: '#ef4444' },
  { key: 'career', name: 'Career', icon: '💼', color: '#3b82f6' },
  { key: 'education', name: 'Education', icon: '📚', color: '#8b5cf6' },
  { key: 'wealth', name: 'Wealth', icon: '💰', color: '#22c55e' },
  { key: 'spiritual', name: 'Spiritual', icon: '🕉️', color: '#a855f7' },
  { key: 'travel', name: 'Travel', icon: '✈️', color: '#06b6d4' },
  { key: 'family', name: 'Family', icon: '👨‍👩‍👧', color: '#f472b6' },
  { key: 'marriage', name: 'Marriage', icon: '💍', color: '#ec4899' },
];

const PLANET_COLORS = {
  Sun: '#FFD700', Moon: '#C8C8FF', Mars: '#DC143C', Mercury: '#32CD32',
  Jupiter: '#FFA500', Venus: '#FF69B4', Saturn: '#8B4513', Rahu: '#4B0082', Ketu: '#006400'
};

const FutureStoryDetailed = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [storyData, setStoryData] = useState(null);
  const [currentDomainIndex, setCurrentDomainIndex] = useState(0);
  const [currentMahadashaIndex, setCurrentMahadashaIndex] = useState(-1); // -1 = showing overview
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [displayedText, setDisplayedText] = useState('');
  
  const audioRef = useRef(null);
  const textIntervalRef = useRef(null);

  // Load story data
  useEffect(() => {
    loadStoryData();
  }, []);

  const loadStoryData = async () => {
    try {
      setLoading(true);
      
      // Get birth data from localStorage
      const birthDetails = localStorage.getItem('birthDetails') || localStorage.getItem('birth_details');
      if (!birthDetails) {
        setError('No birth data found. Please complete onboarding first.');
        setLoading(false);
        return;
      }
      
      const birth = JSON.parse(birthDetails);
      const [year, month, day] = birth.dateOfBirth.split('-').map(Number);
      const [hour, minute] = (birth.timeOfBirth || '12:00').replace(' PM', '').replace(' AM', '').split(':').map(Number);
      
      const response = await fetch(`${API_URL}/api/unified-story/future-story-detailed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: birth.name,
          year, month, day, hour, minute,
          latitude: birth.latitude,
          longitude: birth.longitude,
          gender: birth.gender || 'male'
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        setStoryData(result.data);
        setDisplayedText(result.data.domain_narrations[DOMAINS[0].key]?.overview || '');
      } else {
        setError(result.error || 'Failed to load story data');
      }
    } catch (e) {
      setError(`Error: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Get current domain and narration
  const currentDomain = DOMAINS[currentDomainIndex];
  const currentDomainData = storyData?.domain_narrations?.[currentDomain?.key];
  const currentMahadasha = currentMahadashaIndex >= 0 
    ? currentDomainData?.mahadasha_narrations?.[currentMahadashaIndex]
    : null;

  // Update displayed text when domain/mahadasha changes
  useEffect(() => {
    if (!currentDomainData) return;
    
    if (currentMahadashaIndex === -1) {
      // Show domain overview
      setDisplayedText(currentDomainData.overview || '');
    } else if (currentMahadasha) {
      // Show mahadasha narration
      setDisplayedText(currentMahadasha.narration || '');
    }
  }, [currentDomainIndex, currentMahadashaIndex, currentDomainData, currentMahadasha]);

  // Navigate to next section
  const goToNext = () => {
    if (!currentDomainData) return;
    
    const totalMahadashas = currentDomainData.mahadasha_narrations?.length || 0;
    
    if (currentMahadashaIndex < totalMahadashas - 1) {
      // Next mahadasha in current domain
      setCurrentMahadashaIndex(prev => prev + 1);
    } else if (currentDomainIndex < DOMAINS.length - 1) {
      // Next domain, start with overview
      setCurrentDomainIndex(prev => prev + 1);
      setCurrentMahadashaIndex(-1);
    }
  };

  // Navigate to previous section
  const goToPrevious = () => {
    if (currentMahadashaIndex > -1) {
      // Previous mahadasha or overview
      setCurrentMahadashaIndex(prev => prev - 1);
    } else if (currentDomainIndex > 0) {
      // Previous domain, go to last mahadasha
      const prevDomainData = storyData?.domain_narrations?.[DOMAINS[currentDomainIndex - 1]?.key];
      const prevMahadashaCount = prevDomainData?.mahadasha_narrations?.length || 0;
      setCurrentDomainIndex(prev => prev - 1);
      setCurrentMahadashaIndex(prevMahadashaCount - 1);
    }
  };

  // Select specific domain
  const selectDomain = (index) => {
    setCurrentDomainIndex(index);
    setCurrentMahadashaIndex(-1);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0e17] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-violet-500/30 border-t-violet-500 rounded-full animate-spin mx-auto" />
          <p className="text-white/60 mt-4">Loading your future story...</p>
          <p className="text-white/30 text-sm mt-2">Generating predictions for all 8 domains</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0e17] flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-rose-400 mb-4">{error}</p>
          <button 
            onClick={() => navigate('/home')}
            className="px-4 py-2 bg-violet-500 text-white rounded-lg"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0e17] flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-white/5">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate('/home')} className="text-white/60">
            <ChevronLeft className="w-6 h-6" />
          </button>
          <div className="text-center">
            <h1 className="text-white font-semibold">Future Yatra</h1>
            <p className="text-white/40 text-xs">{storyData?.person_name} • Age {storyData?.current_age}</p>
          </div>
          <div className="w-6" />
        </div>
      </div>

      {/* Domain Selector */}
      <div className="px-4 py-3 overflow-x-auto">
        <div className="flex gap-2 min-w-max">
          {DOMAINS.map((domain, idx) => (
            <button
              key={domain.key}
              onClick={() => selectDomain(idx)}
              className={`px-3 py-2 rounded-xl flex items-center gap-2 transition-all ${
                currentDomainIndex === idx 
                  ? 'bg-white/10 ring-1 ring-white/20' 
                  : 'bg-white/5 hover:bg-white/10'
              }`}
            >
              <span className="text-lg">{domain.icon}</span>
              <span className={`text-xs font-medium ${currentDomainIndex === idx ? 'text-white' : 'text-white/60'}`}>
                {domain.name}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Current Section Indicator */}
      <div className="px-4 py-2">
        <div className="flex items-center justify-between text-xs">
          <span className="text-white/40">
            {currentMahadashaIndex === -1 ? 'Domain Overview' : `Mahadasha ${currentMahadashaIndex + 1}`}
          </span>
          {currentMahadasha && (
            <span className="px-2 py-0.5 rounded text-white/80" style={{ backgroundColor: `${PLANET_COLORS[currentMahadasha.lord]}30` }}>
              {currentMahadasha.lord} • {currentMahadasha.years}
            </span>
          )}
        </div>
      </div>

      {/* Main Content - Narration Display */}
      <div className="flex-1 px-4 py-4 overflow-y-auto">
        <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-2xl p-5 border border-white/5 min-h-[300px]">
          {/* Domain Header */}
          <div className="flex items-center gap-3 mb-4 pb-4 border-b border-white/10">
            <span className="text-3xl">{currentDomain?.icon}</span>
            <div>
              <h2 className="text-white font-semibold text-lg">{currentDomainData?.name}</h2>
              <p className="text-white/40 text-xs">
                {currentMahadashaIndex === -1 
                  ? 'Complete Overview' 
                  : `${currentMahadasha?.lord} Mahadasha (${currentMahadasha?.years})`
                }
              </p>
            </div>
            {currentMahadasha?.status === 'current' && (
              <span className="ml-auto px-2 py-0.5 bg-green-500/20 text-green-400 rounded text-[10px] font-medium">
                CURRENT
              </span>
            )}
          </div>
          
          {/* Narration Text */}
          <div className="text-white/80 text-sm leading-relaxed whitespace-pre-wrap">
            {displayedText}
          </div>
        </div>

        {/* Mahadasha Timeline for Current Domain */}
        {currentDomainData?.mahadasha_narrations && (
          <div className="mt-4">
            <p className="text-white/40 text-xs mb-2 uppercase tracking-wider">Mahadasha Timeline</p>
            <div className="flex gap-1 overflow-x-auto pb-2">
              {/* Overview button */}
              <button
                onClick={() => setCurrentMahadashaIndex(-1)}
                className={`px-3 py-2 rounded-lg text-xs whitespace-nowrap transition-all ${
                  currentMahadashaIndex === -1
                    ? 'bg-violet-500/20 text-violet-300 ring-1 ring-violet-500/40'
                    : 'bg-white/5 text-white/50 hover:bg-white/10'
                }`}
              >
                Overview
              </button>
              
              {currentDomainData.mahadasha_narrations.map((md, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentMahadashaIndex(idx)}
                  className={`px-3 py-2 rounded-lg text-xs whitespace-nowrap transition-all ${
                    currentMahadashaIndex === idx
                      ? 'ring-1 ring-white/40'
                      : 'hover:bg-white/10'
                  }`}
                  style={{
                    backgroundColor: currentMahadashaIndex === idx 
                      ? `${PLANET_COLORS[md.lord]}30` 
                      : 'rgba(255,255,255,0.05)',
                    color: currentMahadashaIndex === idx 
                      ? PLANET_COLORS[md.lord] 
                      : 'rgba(255,255,255,0.5)'
                  }}
                >
                  {md.lord}
                  {md.status === 'current' && <span className="ml-1 text-green-400">●</span>}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Bottom Navigation */}
      <div className="p-4 border-t border-white/5 bg-slate-900/50">
        <div className="flex items-center justify-between">
          <button
            onClick={goToPrevious}
            disabled={currentDomainIndex === 0 && currentMahadashaIndex === -1}
            className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center disabled:opacity-30"
          >
            <ChevronLeft className="w-5 h-5 text-white/60" />
          </button>
          
          <div className="text-center">
            <p className="text-white/30 text-xs">
              Domain {currentDomainIndex + 1} of {DOMAINS.length}
            </p>
            <p className="text-white/50 text-xs">
              {currentMahadashaIndex === -1 ? 'Overview' : `Part ${currentMahadashaIndex + 2} of ${(currentDomainData?.mahadasha_narrations?.length || 0) + 1}`}
            </p>
          </div>
          
          <button
            onClick={goToNext}
            disabled={currentDomainIndex === DOMAINS.length - 1 && currentMahadashaIndex >= (currentDomainData?.mahadasha_narrations?.length || 0) - 1}
            className="w-12 h-12 rounded-full bg-violet-500/20 flex items-center justify-center disabled:opacity-30"
          >
            <ChevronRight className="w-5 h-5 text-violet-400" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default FutureStoryDetailed;
